import React, { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import {
  isPushSupported,
  subscribeToPush,
  getCurrentSubscriptionStatus,
} from '../lib/pushNotifications';

const DISMISS_KEY = 'payiq.push.dismissed.v1';

/** Dismissable banner that nudges the vendor to enable browser
 *  notifications. Only renders when:
 *   - Browser supports web push (modern Chrome/Edge/Firefox)
 *   - Permission hasn't been granted yet (status === 'available')
 *   - User hasn't dismissed before
 *
 *  Permission status is checked once on mount. The flash of nothing
 *  before that resolves is fine — banner is intentionally low-key. */
export const EnableNotificationsBanner: React.FC = () => {
  const [shown, setShown] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    if (!isPushSupported()) return;
    void getCurrentSubscriptionStatus().then((status) => {
      if (status === 'available') setShown(true);
    });
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setShown(false);
  }

  async function handleEnable() {
    setSubmitting(true);
    const ok = await subscribeToPush();
    setSubmitting(false);
    if (ok) {
      // Hide on success — even if the user un-dismisses we don't want
      // to re-prompt while they're already subscribed.
      setShown(false);
    } else {
      // Most likely path: permission denied. Persist dismissal so the
      // banner doesn't keep nagging.
      dismiss();
    }
  }

  if (!shown) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        background: 'var(--blue-100)',
        border: '1px solid var(--blue-200)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 16,
      }}
    >
      <Bell size={18} color="var(--primary)" />
      <div style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)' }}>
        <strong>Enable browser notifications</strong> to get pinged on new
        orders, completed payments, and disputes — even when this tab is in
        the background.
      </div>
      <button
        onClick={handleEnable}
        disabled={submitting}
        style={{
          padding: '8px 14px',
          background: 'var(--primary)',
          color: 'var(--text-inverse)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: 13,
          fontWeight: 600,
          cursor: submitting ? 'wait' : 'pointer',
        }}
      >
        {submitting ? 'Enabling…' : 'Turn on'}
      </button>
      <button
        onClick={dismiss}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          display: 'inline-flex',
        }}
        aria-label="Dismiss"
      >
        <X size={16} color="var(--text-muted)" />
      </button>
    </div>
  );
};
